import React, { useRef, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Camera, Upload, Loader2 } from "lucide-react";
import { fileToJpegDataUrl } from "@/lib/imageCompress";

const TXT = {
  fr: { take: "Prendre une photo", upload: "Importer", busy: "Lecture de l'étiquette…", err: "Impossible de lire cette photo" },
  en: { take: "Take a photo", upload: "Upload", busy: "Reading the label…", err: "Could not read this photo" },
  es: { take: "Hacer una foto", upload: "Importar", busy: "Leyendo la etiqueta…", err: "No se pudo leer esta foto" },
  pt: { take: "Tirar uma foto", upload: "Importar", busy: "A ler o rótulo…", err: "Não foi possível ler esta foto" },
};

/** Camera / upload buttons that turn a label photo into a tasting draft via /tastings/from-photo. */
export default function LabelPhotoPicker({ lang = "fr", onStart, onResult, onDone, compact = false, testIdPrefix = "label-photo" }) {
  const T = TXT[lang] || TXT.fr;
  const camRef = useRef(null);
  const fileRef = useRef(null);
  const [busy, setBusy] = useState(false);

  const onPick = async (file) => {
    if (!file) return;
    setBusy(true);
    let preview = null;
    try {
      preview = await fileToJpegDataUrl(file);
      onStart && onStart(preview);
      const { data } = await api.post("/tastings/from-photo", { image_base64: preview, lang });
      onResult({ ...data, user_photo_url: preview, photo_url: data.photo_url || preview }, preview);
    } catch (e) {
      toast.error(e?.response?.data?.detail || T.err);
    } finally {
      setBusy(false);
      onDone && onDone();
      if (camRef.current) camRef.current.value = "";
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const base = compact
    ? "inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-full border bg-white text-[#1F3A5F] border-[#1F3A5F]/15 hover:border-[#E5276A] hover:text-[#E5276A] transition-colors disabled:opacity-50"
    : "inline-flex items-center gap-2 px-5 py-2.5 rounded-xl text-sm font-bold transition-colors disabled:opacity-50";

  return (
    <div className="inline-flex items-center gap-2 flex-wrap">
      <button type="button" disabled={busy} onClick={() => (compact ? fileRef : camRef).current?.click()} data-testid={`${testIdPrefix}-take`}
              className={compact ? base : `${base} bg-[#E5276A] hover:bg-[#C01661] text-white`}>
        {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Camera className="w-4 h-4" />} {busy ? T.busy : T.take}
      </button>
      {!compact && (
        <button type="button" disabled={busy} onClick={() => fileRef.current?.click()} data-testid={`${testIdPrefix}-upload`}
                className={`${base} bg-white border border-[#1F3A5F]/15 text-[#1F3A5F] hover:border-[#E5276A] hover:text-[#E5276A]`}>
          <Upload className="w-4 h-4" /> {T.upload}
        </button>
      )}
      <input ref={camRef} type="file" accept="image/*" capture="environment" className="hidden"
             data-testid={`${testIdPrefix}-camera-input`} onChange={(e) => onPick(e.target.files?.[0])} />
      <input ref={fileRef} type="file" accept="image/*" className="hidden"
             data-testid={`${testIdPrefix}-file-input`} onChange={(e) => onPick(e.target.files?.[0])} />
    </div>
  );
}
